import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import {
  buildFncoPriceSupplyContentHtml,
  FncoPriceSupplyNote,
  FncoPriceSupplySnapshot,
} from '@/components/Approval/@components/FncoPriceSupply';
import { getFncoPriceSupplyDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import ChevronDownIcon from '@/public/icons/approval/chevron-down.svg';
import ChevronUpIcon from '@/public/icons/approval/chevron-up.svg';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  createFncoPriceSupplyState,
  FNCO_PRICE_SUPPLY_MISSING_MESSAGE,
  FNCO_PRICE_TITLE,
  FNCO_PRICE_SUPPLY_VISIBLE_ROWS,
  getFncoPriceSupplyItems,
  type FncoPriceSupplyDraftSource,
  type FncoPriceType,
} from '@/utils/approval/fncoPriceSupply';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

type FncoPriceSupplyFieldProps = {
  apvType: FncoPriceType;
  /** 신규는 양식 응답, 편집은 저장값 복원이 끝난 뒤 true — 그때 요청번호로 1회 조회 */
  ready: boolean;
};

/**
 * 가격 등록 공급가(197)·할인율(198)·판매가(199) 요청 — 요청번호로 SAP 가격 등록 데이터를 조회해 표로.
 * 편집으로 다시 열어도 저장 표가 아니라 다시 조회(레거시 동일)
 */
const FncoPriceSupplyField = ({
  apvType,
  ready,
}: FncoPriceSupplyFieldProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'fncoPriceSupply' });
  const [expanded, setExpanded] = useState(false);
  const startedRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    const requestNumber = getValues('fncoPriceSupply')?.requestNumber;
    if (!requestNumber) {
      snackbar.showError({ message: t(FNCO_PRICE_SUPPLY_MISSING_MESSAGE) });
      return;
    }
    setValue('fncoPriceSupply', {
      ...createFncoPriceSupplyState(requestNumber),
      loading: true,
    });
    getNestedDataFetcher<FncoPriceSupplyDraftSource>(
      getFncoPriceSupplyDraftSourceUrl(apvType, requestNumber),
    )
      .then((res) => {
        if (!aliveRef.current) return;
        setValue('fncoPriceSupply', {
          ...createFncoPriceSupplyState(requestNumber),
          data: res.attributes?.data ?? res.display?.source ?? null,
        });
      })
      .catch(() => {
        if (!aliveRef.current) return;
        setValue('fncoPriceSupply', createFncoPriceSupplyState(requestNumber));
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
      });
  }, [apvType, ready, getValues, setValue, snackbar, t]);

  const items = useMemo(() => getFncoPriceSupplyItems(state?.data), [state]);
  const collapsible = items.length > FNCO_PRICE_SUPPLY_VISIBLE_ROWS;
  const content = useMemo(
    () =>
      buildFncoPriceSupplyContentHtml({
        apvType,
        items:
          collapsible && !expanded
            ? items.slice(0, FNCO_PRICE_SUPPLY_VISIBLE_ROWS)
            : items,
        t,
        hideNote: true,
      }),
    [apvType, items, collapsible, expanded, t],
  );

  return (
    <FieldBlock>
      <FieldHeader>
        <InfoSectionTitle>{t(FNCO_PRICE_TITLE[apvType])}</InfoSectionTitle>
      </FieldHeader>
      <FncoPriceSupplySnapshot content={content} hideTitle />
      {collapsible && (
        <ToggleButton
          type="button"
          variant="secondary"
          size="small"
          endIcon={
            expanded ? (
              <ChevronUpIcon width={16} height={16} />
            ) : (
              <ChevronDownIcon width={16} height={16} />
            )
          }
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? t('접기') : t('펼치기')}
        </ToggleButton>
      )}
      <FncoPriceSupplyNote apvType={apvType} t={t} />
      {state?.loading && (
        <LoadingMessage>{t('데이터를 불러오는 중입니다...')}</LoadingMessage>
      )}
    </FieldBlock>
  );
};

const ToggleButton = styled(BoxButton)({
  width: '100%',
  marginTop: 8,
});

const LoadingMessage = styled(Body2)({
  marginTop: 16,
  textAlign: 'center',
  color: 'var(--dk-text-sub, #6B7280)',
});

export default FncoPriceSupplyField;
